import { useState, type ReactElement } from 'react'
import {
	Area,
	AreaChart as RechartsAreaChart,
	ReferenceDot,
	ResponsiveContainer,
	XAxis,
	YAxis,
} from 'recharts'
import { cn } from '../../lib/utils'

export interface AreaChartSeries {
	id:    string
	label: string
	color: string
	/** Listed in the tooltip only, no area is drawn (e.g. a derived «gap» value). */
	tooltipOnly?: boolean
}

export interface AreaChartPoint {
	x: string  // label на оси X (например, 'Jan')
	label?: string  // tooltip title; falls back to x
	[seriesId: string]: string | number | undefined
}

export interface AreaChartProps {
	data:      AreaChartPoint[]
	series:    AreaChartSeries[]
	height?:   number
	className?: string
	formatValue?: (value: number) => string
}

interface RawTooltipProps {
	active?:  boolean
	label?:   string | number
	payload?: ReadonlyArray<{
		dataKey?: string | number
		value?:   string | number
		color?:   string
	}>
}

function makeTooltipContent(
	series: AreaChartSeries[],
	formatValue?: (value: number) => string,
) {
	return function TooltipContent(props: RawTooltipProps): ReactElement | null {
		const { active, payload, label } = props
		if (!active || !payload?.length) return null
		return (
			<div className="min-w-44 rounded-(--radius-md) border border-(--color-border) bg-(--color-background) p-2 shadow-(--shadow-md)">
				<div className="mb-1 text-xs font-[500] text-(--color-foreground)">{label}</div>
				<ul className="flex flex-col gap-0.5">
					{payload.map((p) => {
						const meta = series.find((s) => s.id === p.dataKey)
						if (!meta) return null
						const numericValue = typeof p.value === 'number' ? p.value : Number(p.value)
						return (
							<li key={meta.id} className="flex items-center gap-2 text-xs">
								<span aria-hidden className="size-2.5 rounded-[3px]" style={{ backgroundColor: meta.color }} />
								<span className="text-(--color-muted-foreground)">{meta.label}</span>
								<span className="ml-auto font-[500] text-(--color-foreground)">
									{formatValue && Number.isFinite(numericValue) ? formatValue(numericValue) : String(p.value ?? '')}
								</span>
							</li>
						)
					})}
				</ul>
			</div>
		)
	}
}

// Side margins of the plot: they keep the first and last month labels from
// being clipped, and the hover strips below are laid over exactly this area.
const MARGIN_X = 16

export function AreaChart({ data, series, height = 240, className, formatValue }: AreaChartProps) {
	// Recharts' own tooltip does not fire over the X-axis labels, so hover is
	// tracked here with full-height strips (plot + month label).
	const [activeIndex, setActiveIndex] = useState<number | null>(null)
	const TooltipContent = makeTooltipContent(series, formatValue)
	const activePoint = activeIndex != null ? data[activeIndex] : undefined
	// The category axis puts the first point on the left edge and the last on
	// the right one, so each strip is centred on its point, not on an equal slice.
	const step = data.length > 1 ? 100 / (data.length - 1) : 100

	return (
		<div
			className={cn('relative w-full', className)}
			style={{ height }}
			onMouseLeave={() => setActiveIndex(null)}
		>
			<ResponsiveContainer>
				<RechartsAreaChart data={data} margin={{ top: 10, right: MARGIN_X, left: MARGIN_X, bottom: 0 }}>
					<defs>
						{series.map((s) => (
							<linearGradient key={s.id} id={`area-${s.id}`} x1="0" y1="0" x2="0" y2="1">
								<stop offset="0%" stopColor={s.color} stopOpacity={0.4} />
								<stop offset="100%" stopColor={s.color} stopOpacity={0} />
							</linearGradient>
						))}
					</defs>
					<XAxis
						dataKey="x"
						stroke="var(--color-muted-foreground)"
						tick={{ fontSize: 12 }}
						tickLine={false}
						axisLine={false}
						interval="preserveStartEnd"
					/>
					<YAxis hide />
					{series.filter((s) => !s.tooltipOnly).map((s) => (
						<Area
							key={s.id}
							type="monotone"
							dataKey={s.id}
							stroke={s.color}
							strokeWidth={1}
							fill={`url(#area-${s.id})`}
							activeDot={false}
							isAnimationActive={false}
						/>
					))}
					{activePoint && series.filter((s) => !s.tooltipOnly).map((s) => (
						<ReferenceDot
							key={s.id}
							x={activePoint.x}
							y={Number(activePoint[s.id] ?? 0)}
							r={4}
							fill={s.color}
							stroke="var(--color-background)"
							strokeWidth={2}
						/>
					))}
				</RechartsAreaChart>
			</ResponsiveContainer>
			<div
				className="absolute inset-y-0"
				style={{ left: MARGIN_X, right: MARGIN_X }}
			>
				{data.map((point, i) => {
					const onRightHalf = i > (data.length - 1) / 2
					return (
						<div
							key={`${point.x}-${i}`}
							className="absolute inset-y-0"
							style={{
								left:  data.length > 1 ? `${(i - 0.5) * step}%` : 0,
								width: `${step}%`,
							}}
							onMouseEnter={() => setActiveIndex(i)}
						>
							{i === activeIndex && (
								<div
									className={cn(
										'pointer-events-none absolute top-1/4 z-10 whitespace-nowrap',
										onRightHalf ? 'right-1/2 mr-3' : 'left-1/2 ml-3',
									)}
								>
									<TooltipContent
										active
										label={typeof point.label === 'string' ? point.label : point.x}
										payload={series.map((s) => ({ dataKey: s.id, value: point[s.id], color: s.color }))}
									/>
								</div>
							)}
						</div>
					)
				})}
			</div>
		</div>
	)
}
